class ZptStickyAddToCart extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.bundle = this.closest('.shopify-section')?.querySelector('zpt-product-bundles');
    this.submit = this.bundle?.querySelector('[data-bundle-submit]');
    this.stickyBar = this.querySelector('[data-zpt-sticky-bar]');
    this.stickyButton = this.querySelector('[data-sticky-add-to-cart-button]');
    this.stickyLabel = this.querySelector('[data-sticky-add-to-cart-label]');
    this.currentPrice = this.querySelector('[data-sticky-current-price]');
    this.comparePrice = this.querySelector('[data-sticky-compare-price]');
    this.variant = this.querySelector('[data-sticky-variant]');
    this.options = this.querySelector('[data-sticky-options]');
    this.info = this.querySelector('.zpt-sticky-add-to-cart__info');
    this.productImage = this.querySelector('[data-sticky-product-image]');
    this.footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (!this.bundle || !this.submit || !this.stickyBar || !this.stickyButton) {
      this.remove();
      return;
    }

    this.abortController = new AbortController();
    this.stickyButton.addEventListener('click', this.handleClick, {
      signal: this.abortController.signal,
    });
    this.bundle.addEventListener('change', this.syncState, {
      signal: this.abortController.signal,
    });
    this.options?.addEventListener('change', this.handleOptionChange, {
      signal: this.abortController.signal,
    });

    this.buyButtonsObserver = new IntersectionObserver(this.updatePurchaseVisibility);
    this.buyButtonsObserver.observe(this.submit);

    if (this.footer) {
      this.footerObserver = new IntersectionObserver(this.updateFooterVisibility, {
        rootMargin: '200px 0px 0px 0px',
      });
      this.footerObserver.observe(this.footer);
    }

    this.bundleObserver = new MutationObserver(this.syncState);
    this.bundleObserver.observe(this.bundle, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
      attributeFilter: ['class', 'disabled'],
    });

    this.syncState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.buyButtonsObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.bundleObserver?.disconnect();
    clearTimeout(this.addedTimer);
    this.initialized = false;
  }

  handleClick = () => {
    if (this.stickyButton?.disabled || this.isAdding) return;

    const variant = this.getSelectedVariant();
    if (!variant?.available || !variant.id) return;

    this.isAdding = true;
    this.stickyButton.disabled = true;
    this.stickyButton.setAttribute('aria-busy', 'true');

    this.bundle.addSingleItemToCart(variant.id)
      .then(() => {
        this.stickyButton.dataset.added = 'true';
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => {
          this.stickyButton?.removeAttribute('data-added');
        }, 1000);
      })
      .catch(error => {
        console.error('ZPT Sticky Add To Cart:', error);
        window.alert(error.message || 'Could not add this product.');
      })
      .finally(() => {
        this.isAdding = false;
        this.stickyButton?.removeAttribute('aria-busy');
        this.syncState();
      });
  };

  handleOptionChange = event => {
    const select = event.target instanceof Element
      ? event.target.closest('[data-sticky-source-row]')
      : null;
    if (!select) return;

    const tier = this.bundle?.querySelector('[data-bundle-tier].is-active');
    const source = tier?.querySelectorAll('[data-bundle-item]')
      [Number(select.dataset.stickySourceRow)]
      ?.querySelectorAll('.zpt-product-bundles__variant-select')
      [Number(select.dataset.stickySourceOption)];

    if (source && source.value !== select.value) {
      source.value = select.value;
      source.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  syncState = () => {
    const tier = this.bundle?.querySelector('[data-bundle-tier].is-active');
    const config = this.bundle?.config || this.readBundleConfig();
    const row = tier?.querySelector('[data-bundle-item]') || null;
    this.syncOptions(row);

    const variant = row && this.bundle?.findVariant
      ? this.bundle.findVariant(row)
      : config?.variants?.find(item => Number(item.id) === Number(config.fallbackVariantId))
        || config?.variants?.find(item => item.available);

    if (this.variant) {
      const variantLabel = variant?.options?.filter(option => option && option !== 'Default Title').join(' / ');
      const hasSelectableOptions = Boolean(row?.querySelector('.zpt-product-bundles__variant-select'));
      this.variant.textContent = variantLabel || '';
      this.variant.hidden = hasSelectableOptions || !variantLabel;
      this.info.hidden = false;
    }

    if (this.currentPrice) {
      this.currentPrice.textContent = variant ? this.bundle.formatMoney(variant.price) : '';
    }

    if (this.comparePrice) {
      const price = Number(variant?.price) || 0;
      const comparePrice = Number(variant?.compare_at_price) || 0;
      this.comparePrice.textContent = comparePrice > price ? this.bundle.formatMoney(comparePrice) : '';
      this.comparePrice.hidden = comparePrice <= price;
    }

    if (this.productImage && variant?.featured_image?.src) {
      this.productImage.src = variant.featured_image.src;
      this.productImage.alt = variant.featured_image.alt || this.querySelector('.zpt-sticky-add-to-cart__title')?.textContent || '';
    }

    const label = variant?.available
      ? this.submit?.querySelector('[data-bundle-button-text]')?.textContent.trim()
      : variant
        ? this.dataset.soldOutLabel
        : this.submit?.querySelector('[data-bundle-button-text]')?.textContent.trim();
    if (label && this.stickyLabel) {
      this.stickyLabel.textContent = label;
      this.stickyButton.setAttribute('aria-label', label);
    }
    this.stickyButton.disabled = this.isAdding || !variant?.available;
    this.dataset.available = String(!this.stickyButton.disabled);
  };

  getSelectedVariant() {
    const tier = this.bundle?.querySelector('[data-bundle-tier].is-active');
    const row = tier?.querySelector('[data-bundle-item]');
    if (row && this.bundle?.findVariant) return this.bundle.findVariant(row);

    const config = this.bundle?.config || this.readBundleConfig();
    return config?.variants?.find(item => Number(item.id) === Number(config.fallbackVariantId))
      || config?.variants?.find(item => item.available);
  }

  syncOptions(row) {
    if (!this.options) return;

    const sourceSelects = row
      ? [...row.querySelectorAll('.zpt-product-bundles__variant-select')]
      : [];
    const signature = sourceSelects
      .map(select => `${select.getAttribute('aria-label')}:${[...select.options].map(option => option.value).join(',')}`)
      .join('|');

    if (signature !== this.optionsSignature) {
      this.options.replaceChildren();
      if (sourceSelects.length) {
        const optionRow = document.createElement('div');
        optionRow.className = 'zpt-sticky-add-to-cart__option-row';

        const selects = document.createElement('div');
        selects.className = 'zpt-sticky-add-to-cart__option-selects';
        sourceSelects.forEach((source, optionIndex) => {
          const select = document.createElement('select');
          select.className = 'zpt-sticky-add-to-cart__option-select';
          select.dataset.stickySourceRow = '0';
          select.dataset.stickySourceOption = String(optionIndex);
          select.setAttribute('aria-label', source.getAttribute('aria-label') || `Option ${optionIndex + 1}`);

          [...source.options].forEach(option => {
            const clone = document.createElement('option');
            clone.value = option.value;
            clone.textContent = option.textContent;
            select.append(clone);
          });

          selects.append(select);
        });

        optionRow.append(selects);
        this.options.append(optionRow);
      }
      this.optionsSignature = signature;
    }

    const proxySelects = [...this.options.querySelectorAll('[data-sticky-source-row="0"]')];
    sourceSelects.forEach((source, optionIndex) => {
      if (proxySelects[optionIndex] && proxySelects[optionIndex].value !== source.value) {
        proxySelects[optionIndex].value = source.value;
      }
    });
  }

  readBundleConfig() {
    try {
      return JSON.parse(this.bundle?.querySelector('[data-zpt-bundle-config]')?.textContent || '{}');
    } catch {
      return null;
    }
  }

  updatePurchaseVisibility = entries => {
    const [entry] = entries;
    if (!entry) return;

    if (entry.isIntersecting) {
      this.stickyBar.dataset.stuck = 'false';
      this.hiddenByFooter = false;
      return;
    }

    const rect = entry.target.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top < 0) {
      this.stickyBar.dataset.stuck = 'true';
    }
  };

  updateFooterVisibility = entries => {
    const [entry] = entries;
    if (!entry) return;

    if (entry.isIntersecting) {
      this.hiddenByFooter = true;
      this.stickyBar.dataset.stuck = 'false';
      return;
    }

    if (this.hiddenByFooter) {
      const rect = this.submit.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top < 0) {
        this.hiddenByFooter = false;
        this.stickyBar.dataset.stuck = 'true';
      }
    }
  };
}

if (!customElements.get('zpt-sticky-add-to-cart')) {
  customElements.define('zpt-sticky-add-to-cart', ZptStickyAddToCart);
}
